import React, { useEffect } from 'react';
import { usePlaybookDiagnostics } from '../hooks/usePlaybookDiagnostics';

interface DiagnosticsPanelProps {
  instanceId: string;
}

const DiagnosticsPanel: React.FC<DiagnosticsPanelProps> = ({ instanceId }) => {
  const { diagnostics, loading, error, refresh } = usePlaybookDiagnostics(instanceId);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-emerald-600 border-t-transparent" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400">Loading diagnostics...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-200">
        {error}
      </div>
    );
  }

  if (!diagnostics) return null;

  return (
    <div className="space-y-4">
      {/* Current status summary */}
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Current Status</span>
            <span className="font-medium text-gray-900 dark:text-white">{diagnostics.current_status.name}</span>
          </div>
          <div>
            <span className="text-gray-500 dark:text-gray-400 block text-xs">Time in Status</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {diagnostics.time_in_current_status_hours < 1
                ? `${Math.round(diagnostics.time_in_current_status_hours * 60)}m`
                : `${diagnostics.time_in_current_status_hours.toFixed(1)}h`
              }
            </span>
          </div>
        </div>
      </div>

      {/* Stuck indicator */}
      {diagnostics.stuck && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 dark:border-rose-800 dark:bg-rose-900/20">
          <p className="text-sm font-medium text-rose-700 dark:text-rose-200">Instance is stuck</p>
          {diagnostics.stuck_reason && (
            <p className="text-sm text-rose-600 dark:text-rose-300 mt-1">{diagnostics.stuck_reason}</p>
          )}
          {diagnostics.suggested_fixes.length > 0 && (
            <div className="mt-2">
              <p className="text-xs font-semibold text-rose-700 dark:text-rose-200 uppercase tracking-wide mb-1">
                Suggested fixes:
              </p>
              <ul className="space-y-0.5">
                {diagnostics.suggested_fixes.map((fix, i) => (
                  <li key={i} className="flex items-start gap-1.5 text-sm text-rose-600 dark:text-rose-300">
                    <span className="mt-1 h-1 w-1 rounded-full bg-rose-400 flex-shrink-0" />
                    {fix}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Available transitions with guard details */}
      <div>
        <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Transition Guard Evaluations
        </h4>
        {diagnostics.available_transitions.length === 0 ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">No transitions available from current status.</p>
        ) : (
          <div className="space-y-3">
            {diagnostics.available_transitions.map((t) => (
              <div
                key={t.transition_id}
                className={`rounded-lg border p-3 ${
                  t.all_guards_pass
                    ? 'border-emerald-200 bg-emerald-50/50 dark:border-emerald-800 dark:bg-emerald-900/10'
                    : 'border-rose-200 bg-rose-50/50 dark:border-rose-800 dark:bg-rose-900/10'
                }`}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{t.name}</span>
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${
                    t.all_guards_pass
                      ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                      : 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300'
                  }`}>
                    {t.all_guards_pass ? 'All Pass' : 'Blocked'}
                  </span>
                </div>
                <div className="space-y-1">
                  {t.guards.map((g, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <span className={`h-1.5 w-1.5 rounded-full flex-shrink-0 ${g.passed ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                      <span className={`font-medium ${g.passed ? 'text-gray-600 dark:text-gray-400' : 'text-rose-600 dark:text-rose-400'}`}>
                        {g.guard_type}
                      </span>
                      <span className="text-gray-400 dark:text-gray-500">{g.reason}</span>
                    </div>
                  ))}
                  {t.guards.length === 0 && (
                    <span className="text-xs text-gray-400 dark:text-gray-500">No guards configured</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default DiagnosticsPanel;
